/**
 * The API-keys page (route `/api-keys`; Feature: terraform-provider, task 7; **Requirement 11**;
 * Design: §Design decision 7, §Components 9).
 *
 * Where a Terraform pipeline gets its credential. Built on the `MembersPage` structure — one
 * `LoadState`, {@link ConfirmDialog} in front of each destructive write, a page-level `role="status"`
 * notice, {@link useWrite} so a failure cannot touch the list — with four differences that are specific
 * to a surface that mints credentials.
 *
 * ## The whole page is admin-only, and that is a correction to the obvious design
 *
 * `MembersPage` looks like the template and its gate is the wrong shape here. There, the *read* is
 * ungated and only the writes are gated, because knowing who can see your data is not privileged. Here
 * `api-key:read` is granted to `admin` alone (`ROLE_GRANTS`, `services/src/api/authorization.ts`) — it
 * is the one place the matrix stops being nested by rank — so a viewer or an editor cannot list keys at
 * all. "Viewer sees, admin acts" is not available.
 *
 * So the gate is on the page, and it withholds the **request** as well as the affordances: a non-admin
 * gets the Requirement 11.2 explanation and **no `GET` is issued**. Rendering the table optimistically
 * and letting the `403` produce an error panel would tell a viewer that something is broken, when the
 * true answer is that key management belongs to an administrator. An error message where an explanation
 * belongs is a worse answer than no request.
 *
 * The role comes from {@link useSelectedAccount} — the role **in the selected Account** — and never from
 * the ID token, whose `role` is the home Account's. Reading the claim would show every affordance to
 * someone who is `admin` at home and `viewer` here, and then refuse all four calls.
 *
 * ## The Account is named because the switcher can change it
 *
 * A key is minted inside the credential's own Account (there is no `accountId` in the create body — a
 * body naming another Account is a `403`), and the rail's switcher decides which Account that is. So the
 * page says which one, out loud (Requirement 11.3). It says it by **id**, because an id is the only name
 * an Account has: `AccountSummary` carries `accountId`, `role`, and `via`, and nothing the platform
 * stores is a display name.
 *
 * ## The revealed value lives in this component's state and nowhere else
 *
 * `apiKeyValue` is unrecoverable once this page stops rendering it, so Requirement 11.5 forbids browser
 * storage of any kind. It is held in the {@link revealed} state below — not `localStorage`, not
 * `sessionStorage`, not the URL, not a ref that outlives the render — and dropped when the dialog is
 * dismissed. Unmounting discards it because state is the only place it was.
 *
 * ## Rotation and revocation state their consequence before they happen
 *
 * Rotation invalidates the previous value immediately: the overlap window is
 * `API_KEY_ROTATION_OVERLAP_SECONDS` and its default is `0`, which puts
 * `previousHashRevokeAtEpoch` at the rotation instant itself. Revocation is terminal. Both dialogs say
 * so in the sentence a reader has to pass to confirm, and both surface the API's own refusal verbatim —
 * the `409` on rotating a revoked key most of all, because the platform knows why it refused and this
 * page does not.
 */

import { useEffect, useState } from 'react';

import { useSelectedAccount } from '../account-context';
import { ApiError } from '../api/client';
import type { ApiKeyDto, CreateApiKeyPayload } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { ApiKeyCreateForm } from '../components/ApiKeyCreateForm';
import { ApiKeyRevealDialog, type RevealedApiKey } from '../components/ApiKeyRevealDialog';
import { ApiKeysTable } from '../components/ApiKeysTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { TerraformSetup } from '../components/TerraformSetup';
import { useWrite } from '../lib/use-write';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; apiKeys: ApiKeyDto[] };

/** The one lifecycle write awaiting confirmation. A union, because only one dialog is ever open. */
type PendingAction =
  | { kind: 'rotate'; key: ApiKeyDto }
  | { kind: 'revoke'; key: ApiKeyDto };

export function ApiKeysPage(): JSX.Element {
  const client = useApiClient();
  // The role in the SELECTED Account, not the token's. `api-key:read` is admin-only, so this gates the
  // read as well as the writes — see the module doc.
  const { accountId, role } = useSelectedAccount();
  const isAdmin = role === 'admin';

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingAction | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  /**
   * The one-time API_Key_Value, for as long as its dialog is open. Component state and nothing else
   * (Req 11.5).
   */
  const [revealed, setRevealed] = useState<RevealedApiKey | undefined>(undefined);

  // Two runners: the create form and the confirmation dialogs fail independently, and the two dialogs
  // are mutually exclusive so they can share one.
  const create = useWrite();
  const lifecycle = useWrite();

  useEffect(() => {
    // Req 11.2 — the gate withholds the request, not just the table. A non-admin issues no `GET`.
    if (!isAdmin) return;
    let active = true;
    setState({ status: 'loading' });
    client
      .listApiKeys()
      .then((response) => {
        if (active) setState({ status: 'loaded', apiKeys: response.apiKeys });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load API keys';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client, isAdmin, reloadToken]);

  /** Re-read the list. Only ever called after the API confirmed a write (Req 11.8). */
  function reload(): void {
    setReloadToken((token) => token + 1);
  }

  function openAction(next: PendingAction): void {
    lifecycle.reset();
    setNotice(undefined);
    setPending(next);
  }

  function closeAction(): void {
    lifecycle.reset();
    setPending(undefined);
  }

  function submitCreate(payload: CreateApiKeyPayload): void {
    setNotice(undefined);
    void create.run(
      () => client.createApiKey(payload),
      (response) => {
        setRevealed({
          name: response.apiKey.name,
          value: response.apiKeyValue,
          reason: 'created',
        });
        setNotice(
          `Created ${response.apiKey.name} as ${response.apiKey.role}. Copy the value now — it cannot be shown again.`,
        );
        reload();
      },
    );
  }

  function confirmPending(action: PendingAction): void {
    if (action.kind === 'rotate') {
      void lifecycle.run(
        () => client.rotateApiKey(action.key.keyId),
        (response) => {
          setPending(undefined);
          setRevealed({
            name: response.apiKey.name,
            value: response.apiKeyValue,
            reason: 'rotated',
          });
          setNotice(
            `Rotated ${response.apiKey.name}. The previous value stopped working immediately, so anything still using it needs the replacement.`,
          );
          reload();
        },
      );
      return;
    }
    void lifecycle.run(
      () => client.revokeApiKey(action.key.keyId),
      () => {
        setPending(undefined);
        setNotice(
          `Revoked ${action.key.name}. It can no longer authenticate, and it stays listed so it remains auditable.`,
        );
        reload();
      },
    );
  }

  return (
    <section>
      <h1>API keys</h1>

      {isAdmin ? (
        <>
          {/* Req 11.3 — which Account this mints for, because the rail's switcher decides that. */}
          <p className="muted" data-testid="account-line">
            Keys minted here belong to <strong>{accountId ?? 'this account'}</strong>, and they can read
            and write everything in it. They are not tied to your own membership.
          </p>

          {notice !== undefined ? (
            <p role="status" className="muted">
              {notice}
            </p>
          ) : null}

          <ApiKeysBody
            state={state}
            onRotate={(key) => openAction({ kind: 'rotate', key })}
            onRevoke={(key) => openAction({ kind: 'revoke', key })}
          />

          <h2>Create an API key</h2>
          <ApiKeyCreateForm
            submitting={create.state.submitting}
            fieldErrors={create.state.fieldErrors}
            errorMessage={create.state.errorMessage}
            onSubmit={submitCreate}
          />

          {/*
           * Requirement 14.6 — the provider configuration, on the page where the credential it needs is
           * minted. Below the create form deliberately: the order of operations is mint the key, then
           * configure the provider with it, and the page reads in that order.
           *
           * Inside the admin branch, because that is the only branch that renders anything at all here —
           * a non-admin gets the Requirement 11.2 explanation and no page content. They are not left
           * without the snippet: every entity's Terraform section carries a collapsed copy of it, which is
           * the second half of 14.6 and the reason that half exists.
           */}
          <TerraformSetup />
        </>
      ) : (
        <p role="note" className="muted" data-testid="admin-only-notice">
          API keys are managed by an account administrator. Your role in this account cannot list,
          create, rotate, or revoke them, so nothing is shown here. Ask an administrator of{' '}
          {accountId ?? 'this account'} for a key, and they can hand you one without giving you access
          to this page.
        </p>
      )}

      {pending !== undefined ? (
        <ApiKeyLifecycleDialog
          action={pending}
          busy={lifecycle.state.submitting}
          errorMessage={lifecycle.state.errorMessage}
          onConfirm={() => confirmPending(pending)}
          onCancel={closeAction}
        />
      ) : null}

      {revealed !== undefined ? (
        <ApiKeyRevealDialog revealed={revealed} onDismiss={() => setRevealed(undefined)} />
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The listing                                                                 */
/* -------------------------------------------------------------------------- */

function ApiKeysBody({
  state,
  onRotate,
  onRevoke,
}: {
  state: LoadState;
  onRotate: (key: ApiKeyDto) => void;
  onRevoke: (key: ApiKeyDto) => void;
}): JSX.Element {
  if (state.status === 'loading') return <LoadingState label="Loading API keys…" />;
  if (state.status === 'error') {
    return (
      <ErrorState
        summary="Could not load API keys."
        message={state.message}
        isAuthError={state.isAuthError}
      />
    );
  }
  return <ApiKeysTable keys={state.apiKeys} onRotate={onRotate} onRevoke={onRevoke} />;
}

/* -------------------------------------------------------------------------- */
/* Confirmations (Req 11.7)                                                    */
/* -------------------------------------------------------------------------- */

function ApiKeyLifecycleDialog({
  action,
  busy,
  errorMessage,
  onConfirm,
  onCancel,
}: {
  action: PendingAction;
  busy: boolean;
  errorMessage?: string;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  if (action.kind === 'rotate') {
    return (
      <ConfirmDialog
        title="Rotate API key"
        // The consequence, stated as the default behaves: the overlap is
        // `API_KEY_ROTATION_OVERLAP_SECONDS` and it defaults to 0, so "immediately" is literal.
        message={`Rotating ${action.key.name} issues a new value and invalidates the current one immediately. Anything still using the old value — a Terraform pipeline, a script, a scheduled job — stops authenticating at its next request until it is given the replacement. The key id and its name do not change.`}
        confirmLabel="Rotate key"
        busy={busy}
        // A revoked key comes back as a `409` with the API's own message, which lands here (Req 11.7).
        errorMessage={errorMessage}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ConfirmDialog
      title="Revoke API key"
      message={`Revoking ${action.key.name} is final. The value stops authenticating at its next request, it cannot be restored, and the key cannot be rotated afterwards — a replacement has to be a new key. The record stays in the list so it remains auditable.`}
      confirmLabel="Revoke key"
      busy={busy}
      errorMessage={errorMessage}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
